'use strict';

const performanceMeasure = require('../lib/performanceMeasure');
const adUtil = require('../lib/adUtil');

const IS_EMPTY = 'is-empty';
const IS_LOADED = 'is-loaded';
const NOT_FILLED = 'not-filled';
// This is both top and bottom header ad margin
// Should be equal to $phx_gap in htdocs/styles/base/_mixins.scss
const HEADER_AD_MARGIN = 20;
const INDEX_CAP = 4;
// When ad zones compete, ones that refresh must beat non-refreshing zones
// by this a multiplier (configurable by site). This is a default in case
// that setting is 0 or null.
const REFRESH_OUTBID_FACTOR_DEFAULT = 6;
const FIXED_BOTTOM_ZONE = 'fixed_bottom';
const ADHESION_ZONE = 'adhesion';

function Session(opts) {
    opts = opts || {};

    // these are not actually optional
    this.adServerPromise = opts.adServerPromise;
    this.breakpointPromise = opts.breakpointPromise;
    this.clientPageModelPromise = opts.clientPageModelPromise;
    this.document = opts.document;
    this.adAuctionPromise = opts.adAuctionPromise;
    this.timedOutPageLoadPromise = opts.timedOutPageLoadPromise;
    this.disableAdRendering = opts.disableAdRendering;
    this.undelayedAdCall = opts.undelayedAdCall;

    // If set, all slots will immediately resolve as empty and the ad server
    // will not be called at all.
    this.forceAllSlotsEmpty = !!opts.forceAllSlotsEmpty;

    this.debugLog = opts.debugLog || function () {};
    var debugLog = this.debugLog; // allow references to be minified
    debugLog('new Session with options', opts);

    var self = this;

    // Incremented for each new call to beginPage so that we can detect when
    // an async result arrives for an older page than we're currently dealing
    // with, if the user happens to navigate too quickly for us to keep up.
    self.pageId = 0;

    // Incremented for each dynamicCall to ensure a unique id we can use in
    // ad server APIs for call context.
    self.dynamicCallId = 0;

    // For coordinating the existence of a page and initial page config
    var startSession;
    var sessionStarted = new Promise(function (resolve, reject) {
        startSession = resolve;
    });

    var page;

    self.beginPage = function beginPage(config) {
        var pageId = ++self.pageId;
        var measure = performanceMeasure('advertising:beginPage:' + pageId);
        if (page) {
            page.close();
        }

        function zoneIsConfigured(zone) {
            return config.sizedSlots.some((slot) => slot.model.zone === zone);
        }
        self.hasAdhesionAndFixedBottomZone =
            zoneIsConfigured(ADHESION_ZONE) && zoneIsConfigured(FIXED_BOTTOM_ZONE);

        debugLog('begin page', pageId, config);
        page = new Page(self, pageId, config);

        if (pageId === 1) {
            // A bit of coordination so dynamicCall can be assured of a page
            // (perhaps it might be better to export Session and pass the
            // session-level config there)
            startSession();
        }

        debugLog('page', pageId, 'initial ad call');
        return page.makeInitialAdCall().then(
            function (res) {
                debugLog('page', pageId, 'initial ad call result:', res);
                measure.end();
                return res;
            },
            function (err) {
                debugLog('page', pageId, 'initial ad call failed:', err);
                measure.end();
                return Promise.reject(err);
            }
        );
    };

    self.getPage = function () {
        return sessionStarted.then(function () {
            return page;
        });
    };

    /**
     * Builds the ad request config for an ad slot.
     *
     * @param {Object} slotModel The slot model for just this slot.
     * @param {Array.<Array.<number>>} sizes The sizes to request.
     * @returns {Object} The ad request config.
     */
    self.getAdCallConfig = function getAdCallConfig(slotModel, sizes) {
        return Promise.all([self.adServerPromise, sessionStarted]).then(function (results) {
            var adServer = results[0],
                globalConfig = page.config.global.config;

            return adServer.getAdCallConfig(slotModel, sizes, globalConfig);
        });
    };

    self.resetAdSlot = function resetAdSlot(slotId) {
        self.adServerPromise.then(function (adServer) {
            adServer.resetAdSlot(slotId);
        });
    };

    self.dynamicCall = function dynamicCall(opts) {
        var dynamicCallId = ++self.dynamicCallId;
        debugLog('dynamic call', dynamicCallId, opts);
        var measure = performanceMeasure('advertising:dynamicCall:' + dynamicCallId);
        return sessionStarted
            .then(function () {
                return page.makeDynamicAdCall(dynamicCallId, opts).then(function (res) {
                    debugLog('dynamic call', dynamicCallId, 'result:', res);
                    measure.end();
                    return res;
                });
            })
            .catch(function (err) {
                debugLog('dynamic call', dynamicCallId, 'failed:', err);
                measure.end();
                return Promise.reject(err);
            });
    };

    // getSlotResult returns a promise for the result of loading the slot
    // with the given id on the current page. This can be used to take
    // dynamic actions based on the result, e.g. from directives in the
    // AngularJS app.
    //
    // If the given slot id doesn't match a slot on the current page then
    // the returned promise will never resolve. If the page load is cancelled
    // by navigation before the ad can be loaded, or if an error occurs while
    // attempting to fill the slot, the promise will be rejected.
    self.getSlotResult = function getSlotResult(slotId) {
        if (!page) {
            throw new Error('cannot call getSlotResult before a page is active');
        }

        return page.getSlotResult(slotId);
    };

    self.reloadAd = function reloadAd(slotConfig, auctionId) {
        debugLog('inside reloadAd', { slotConfig, auctionId });
        // Pre-emptively destroy an existing Kargo ad
        // in the FIXED_BOTTOM_ZONE
        if (slotConfig.zone === FIXED_BOTTOM_ZONE && window.Kargo?.CreativeRegister) {
            debugLog('removing an existing Kargo ad');
            window.Kargo.CreativeRegister.getCreativesOfType('Hover')?.[0].destroy();
        }
        return self
            .getSlotResult(slotConfig.id)
            .then(function () {
                debugLog('inside reloadAd getSlotResult callback', { slotConfig, auctionId });
                self.resetAdSlot(slotConfig.id);
                return self.dynamicCall({
                    slots: [slotConfig],
                    auctionId: auctionId,
                });
            })
            .catch(function (err) {
                // There was an error running the auction, so get
                // an ad without using the auction result
                // eslint-disable-next-line no-console
                console.warn(err);
                self.dynamicCall({
                    slots: [slotConfig],
                });
            });
    };
}

// Page facilitates ad calls for a user's interaction with a "page" in
// our single page app. It is responsible for telling the ad server
// we have started a new page, e.g. when a user clicks on a link,
// and also make sure it still the current page as it handles ad requests.
function Page(session, pageId, config) {
    var self = this;

    self.pageId = pageId;
    self.config = config;

    var debugLog = session.debugLog;

    var slotPromises = {};
    var pendingSlotDefers = {};

    function current() {
        return pageId === session.pageId;
    }

    debugLog('adServer.initPage');
    var adServerReady = session.adServerPromise.then(function (adServer) {
        if (!current()) {
            throw new Error('Page not current');
        } else {
            return adServer.initPage().then(function () {
                debugLog('adServer.initPage completed');
                return adServer;
            });
        }
    });

    self.makeInitialAdCall = function makeInitialAdCall() {
        let adAuction;
        return session.adAuctionPromise
            .then(function (auction) {
                adAuction = auction;
                return auction.getLatestResult();
            })
            .then(function (auctionResult) {
                var allSlots = [];
                // The slot ids in the config are not unique across the
                // requests to the same page because they are in the
                // server-side rendered html, thus they are cached.
                // So, the slots should always be reset when a new page starts
                // in our single page app so that the initial ad requests will
                // be made every time.
                config.sizedSlots.forEach(function (slot) {
                    session.resetAdSlot(slot.id);
                    // Exclude deferred-load ads from the initial ad request;
                    // these are loaded when their respective event occurs.
                    if (!slot.model.deferLoad) {
                        allSlots.push(slot);
                    }
                });

                // The slots for out-of-page ads get reset here as well, but
                // the ad call is delayed until we know if there is a certain
                // interstitial that takes precedence over DFP interstitials.
                // (ex. promo interstitial, qualtrics)
                if (config.outOfPageSlots) {
                    config.outOfPageSlots.forEach(function (slot) {
                        session.resetAdSlot(slot.id);
                        allSlots.push(slot);
                    });
                }
                return makeAdCall(allSlots, adAuction, auctionResult);
            });
    };

    self.makeOutOfPageAdCall = function makeOutOfPageAdCall() {
        let adAuction;
        return session.adAuctionPromise
            .then(function (auction) {
                adAuction = auction;
                return auction.getLatestResult();
            })
            .then(function (auctionResult) {
                return makeAdCall(config.outOfPageSlots || [], adAuction, auctionResult);
            });
    };

    self.makeDynamicAdCall = function makeDynamicAdCall(dynamicCallId, opts) {
        let adAuction;
        return session.adAuctionPromise
            .then(function (auction) {
                adAuction = auction;
                if (opts.auctionId) {
                    return auction.getResultById(opts.auctionId);
                } else {
                    return auction.getLatestResult();
                }
            })
            .then(function (auctionResult) {
                return makeAdCall(opts.slots, adAuction, auctionResult);
            })
            .catch(function (err) {
                return makeAdCall(opts.slots, adAuction, null);
            });
    };

    self.getSlotResult = function getSlotResult(id) {
        return makeSlotPromise(id);
    };

    self.close = function close() {
        debugLog('close page', pageId);

        // Reject any slot result promises that are pending, to avoid
        // leaking closures, etc.
        Object.keys(pendingSlotDefers).map(function (id) {
            pendingSlotDefers[id].reject(new Error('cancelled by navigation'));
        });
    };

    // A function built to force a site with both adhesion and fixed_bottom zones
    // to be requested in parallel, instead of separately (so this will cause an
    // ad request for adhesion to wait for the ad request for fixed_bottom, if it
    // was deferred).
    let deferredZones = {};
    let maxBids = {};
    let adhesionWon = false;
    let fixedBottomRequestCount = 0;
    function removeFixedBottomAd(elementId) {
        session.resetAdSlot(elementId);
        let element = session.document.getElementById(elementId);
        if (element) {
            let adElement = element.closest('phoenix-ad');
            if (adElement) {
                adElement.parentElement.removeChild(adElement);
            }
        }
    }

    function adhesionPlusFixedBottomFilter(slots, auctionResult) {
        // If both the fixed_bottom and adhesion units are on the page we want choose the one
        // that has the highest potential yield. Find the one with the higher bid (taking the
        // refresh factor into account). Unless the refreshing unit is in the page and there
        // are no bids then we want to keep it (for AdX).
        function determineZoneWinner() {
            fixedBottomRequestCount++;
            let refreshOutbidFactor = self.config.global.config.expectedRefreshCount;
            if (refreshOutbidFactor == null) {
                // If the configured setting is null (or undefined, which is why we use
                // the == operator here), then use the default
                refreshOutbidFactor = REFRESH_OUTBID_FACTOR_DEFAULT;
            }

            if (!(FIXED_BOTTOM_ZONE in maxBids)) {
                return ADHESION_ZONE;
            } else if (maxBids[FIXED_BOTTOM_ZONE] === 0) {
                // Even though there was no bid, we want to keep this unit
                // and remove the other
                return FIXED_BOTTOM_ZONE;
            }
            if (FIXED_BOTTOM_ZONE in maxBids && ADHESION_ZONE in maxBids) {
                if (
                    maxBids[ADHESION_ZONE] <=
                    maxBids[FIXED_BOTTOM_ZONE] *
                        Math.max(refreshOutbidFactor - (fixedBottomRequestCount - 1), 1)
                ) {
                    return FIXED_BOTTOM_ZONE;
                } else {
                    return ADHESION_ZONE;
                }
            }

            return FIXED_BOTTOM_ZONE;
        }

        if (deferredZones) {
            // collect slot requests for these two competing zones
            let newSlots = slots.filter((slot) => {
                let zone = slot.model.zone;
                if (zone === ADHESION_ZONE || zone === FIXED_BOTTOM_ZONE) {
                    deferredZones[zone] = slot;
                    if (auctionResult) {
                        maxBids[zone] = auctionResult.getMaxBidForAdUnit(slot.id);
                    }
                    return false;
                }
                return true;
            });

            // once we have both zones for a request, consider them together
            if (FIXED_BOTTOM_ZONE in deferredZones && ADHESION_ZONE in deferredZones) {
                // Some zones may not be compatible to run together. Using the maxbids
                // determine which ones we want to remove
                let winner = determineZoneWinner();
                newSlots.push(deferredZones[winner]);
                delete deferredZones[winner];

                // If the adhesion zone wins, we will destroy the fixed_bottom ad slot
                // and it will be excluded from any future ad requests.
                if (winner === ADHESION_ZONE) {
                    adhesionWon = true;
                    window.dispatchEvent(new window.CustomEvent('phoenix:advertising:adhesionWon'));
                    if (deferredZones[FIXED_BOTTOM_ZONE]) {
                        // clean up any potential markup for fixed bottom
                        removeFixedBottomAd(deferredZones[FIXED_BOTTOM_ZONE].id);
                    }

                    // set this to undefined now, we won't filter zones going forward again
                    deferredZones = undefined;
                }
            }

            return newSlots;
        } else {
            // we already handled the competing zone case; but if the adhesion slot won
            // prevent future slot requests for fixed_bottom
            if (adhesionWon) {
                slots = slots.filter((slot) => slot.model.zone !== FIXED_BOTTOM_ZONE);
            }
            return slots;
        }
    }

    function makeAdCall(slots, adAuction, auctionResult) {
        if (session.forceAllSlotsEmpty) {
            debugLog('all slots on page', pageId, 'forced empty by configuration');
            return makeEmptyResponses(slots);
        }
        const gdpr = window.GdprStatus;
        // Configurable value in tempest: Page Load Timeout
        const pageLoadTimeout = config?.global?.config?.pageLoadTimeout ?? 12000;
        const timedOutPageLoadPromise = session.undelayedAdCall
            ? Promise.resolve()
            : window.getTimedOutPageLoadPromise(pageLoadTimeout);
        const dataPromises = [
            adServerReady,
            session.clientPageModelPromise,
            session.breakpointPromise,
            // determine whether we are under GDPR constraints
            gdpr.isApplicable(),
            // country code, for geographic restrictions in bid request configs
            gdpr.countryCodeSafe(),

            // This promise is an important Page Load Time optimization.
            // Ads, by loading additional resources (scripts, iframes, images, ...),
            // delay the window.load event, which penalize us on Google Search and
            // elsewhere. The strategy here is to run the auction asap, but
            // to delay the ad display until after window.load.
            timedOutPageLoadPromise,
        ];
        return Promise.all(dataPromises).then(function (results) {
            if (!current()) {
                debugLog('ignoring prereqs for defunct page', pageId);
                Promise.reject('Page not current');
                return;
            }
            var adServer = results[0];
            var clientPageModel = results[1];
            var breakpoint = results[2];
            var gdprIsApplicable = results[3];

            debugLog('requesting for page', pageId, 'slots with screenSize', breakpoint);
            let pageModel = adUtil.makePageModel(
                config.global.baseModel,
                (adAuction && adAuction.keyValues) || {},
                gdprIsApplicable,
                clientPageModel
            );

            var getZoneIndex = function getZoneIndex(baseModel, model) {
                let index = Math.min(model.index, INDEX_CAP);
                return {
                    zoneIndex: model.zone + '_' + index,
                    siteZoneIndex: baseModel.hostname + '_' + model.zone + '_' + index,
                };
            };

            var slotRequests = slots.filter(function (slot) {
                return !slot.screenSizes || slot.screenSizes.indexOf(breakpoint) !== -1;
            });

            let slotResultHandler = handleSlotResult;
            if (session.hasAdhesionAndFixedBottomZone) {
                // Applies logic for handling the competing fixed_bottom/adhesion zones.
                // This could affect the slots requested, potentially deferring one until later.
                // This is done this after the screen size filter to reduce the number unusable
                // slot requests from consideration.

                // This filter is _not_ run when the `use-late-adhesion-test` is enabled,
                // since we will defer zone resolution after the ad request occurs.
                if (window.phxTrackedFeatures['use-late-adhesion-test'] && deferredZones) {
                    slotResultHandler = handleSlotResultWithLateAdhesionCheck;
                    slotRequests.forEach((slot) => {
                        if ([FIXED_BOTTOM_ZONE, ADHESION_ZONE].includes(slot.model.zone)) {
                            deferredZones[slot.model.zone] = slot;
                        }
                    });
                } else {
                    slotRequests = adhesionPlusFixedBottomFilter(slotRequests, auctionResult);
                }
            }

            slotRequests = slotRequests
                .map(function (slot) {
                    var auctionSlotModel = {};
                    if (auctionResult) {
                        auctionSlotModel = auctionResult.getSlotModelPropertiesForAdUnit(slot.id);
                    }
                    var element = session.document.getElementById(slot.id);
                    if (!element) {
                        // eslint-disable-next-line no-console
                        console.warn('missing container element for ad', slot.id);
                        return null;
                    }
                    return {
                        id: slot.id,
                        adUnitPath: slot.adUnitPath,
                        config: slot.config,
                        element: element,
                        model: Object.assign(
                            {},
                            slot.model,
                            getZoneIndex(config.global.baseModel, slot.model),
                            auctionSlotModel
                        ),
                    };
                })
                .filter(function (req) {
                    return !!req;
                });

            slotRequests.forEach(function (slotRequest) {
                var i;
                // The SSR code can't detect the current breakpoint, so it renders a separate slot
                // for each breakpoint supported in this position, with CSS classes that hide the
                // slot at other breakpoints. Now that we know what breakpoint we're filling this
                // slot for, we want to remove the others from the DOM, and remove the classes
                // that would hide this slot. This means that when the viewport is resized, the
                // initially loaded slot will remain visible even if the ad unit is not the right
                // size for the current breakpoint, which is the desired behavior for simplicity's
                // sake and because resizing is considered an edge case.
                if (slotRequest.model.isOutstream) {
                    return;
                }
                const slotElement = slotRequest.element.parentElement;
                const containerElement = slotElement?.parentElement;
                if (!slotElement || !containerElement) {
                    return;
                }

                var groupSelector =
                    '[data-ad-group="' + containerElement.getAttribute('data-ad-group') + '"]';
                var nodesToRemove = session.document.querySelectorAll(
                    groupSelector + '.not-size-' + breakpoint
                );
                var supportedSizes = {};
                // N.B. We're doing the loop and the remove this way because IE 11's NodeList
                // doesn't support forEach() and nodes don't support remove().
                for (i = 0; i < nodesToRemove.length; i++) {
                    var node = nodesToRemove[i];
                    // We *do* want to hide this slot on resize if the new size isn't supported
                    // at all and the result of continuing to show it would be a weird layout
                    // instead of just an inappropriately sized ad (i.e. a sidebar ad at A/B).
                    // So here, before removing a slot, we infer which size it was for.
                    ['A', 'B', 'C', 'D'].forEach(function (size) {
                        if (!node.classList.contains('not-size-' + size)) {
                            supportedSizes[size] = true;
                        }
                    });
                    node.parentElement.removeChild(node);
                }

                var nodes = session.document.querySelectorAll(groupSelector);
                for (i = 0; i < nodes.length; i++) {
                    Object.keys(supportedSizes).forEach(function (size) {
                        var visClass = 'not-size-' + size;
                        nodes[i].classList.remove(visClass);
                        slotElement.classList.remove(visClass);
                    });
                }
            });

            // Add a then-able wrapper preventing us from progressing further
            // into the ad cycle; we want to block on even requesting ads from
            // the ad server until we are visible.
            let visibilityBlock = new Promise((resolve) => {
                if (document.visibilityState !== 'visible') {
                    let onVisibilityChange = (ev) => {
                        if (document.visibilityState === 'visible') {
                            document.removeEventListener('visibilitychange', onVisibilityChange);
                            resolve(true);
                        }
                    };
                    document.addEventListener('visibilitychange', onVisibilityChange, false);
                } else {
                    // We're already visible, so no need to add a listener.
                    resolve(true);
                }
            });
            return visibilityBlock.then(() => {
                debugLog('adServer.requestAds', slotRequests);
                return adServer
                    .requestAds(config.global.config, pageModel, slotRequests, auctionResult, visibilityBlock)
                    .then(function (promises) {
                        debugLog('adServer.requestAds callback', promises);
                        return promises.map(function (promise) {
                            return promise.then(function (ad) {
                                debugLog('adServer.requestAds after-callback slotResultHandler on ad', ad);
                                if (ad) {
                                    slotResultHandler(ad);
                                }
                                return ad;
                            });
                        });
                    })
                    .then(function (promises) {
                        return Promise.all(promises);
                    });
            });
        });
    }

    function makeEmptyResponses(slots) {
        var promises = slots.map(function (slot) {
            var element = session.document.getElementById(slot.id);
            if (!element) {
                return Promise.reject(new Error('missing container element for ad ' + slot.id));
            }
            return Promise.resolve({
                slotId: slot.id,
                element: element,
                filled: false,
                sizeCode: null,
            }).then(function (ad) {
                handleSlotResult(ad);
                return ad;
            });
        });

        return Promise.all(promises);
    }

    let deferredAds = {};
    function handleSlotResultWithLateAdhesionCheck(ad) {
        if (deferredZones && deferredZones[ADHESION_ZONE] && deferredZones[FIXED_BOTTOM_ZONE]) {
            // If both adhesion and fixed-bottom zones are deferred
            let fixedBottomAd = deferredAds[FIXED_BOTTOM_ZONE];
            let adhesionAd = deferredAds[ADHESION_ZONE];
            if (ad.slotId === deferredZones[ADHESION_ZONE].id) {
                adhesionAd = ad;
                deferredAds[ADHESION_ZONE] = ad;
            } else if (ad.slotId === deferredZones[FIXED_BOTTOM_ZONE].id) {
                fixedBottomAd = ad;
                deferredAds[FIXED_BOTTOM_ZONE] = ad;
            }
            if (fixedBottomAd && adhesionAd) {
                if (adhesionAd.filled && !adhesionAd.responseInfo.dfp.isBackfill) {
                    // Give preference to the adhesion ad slot when it is filled
                    // (but not from backfill).
                    ad = adhesionAd;
                    adhesionWon = true;
                    window.dispatchEvent(new window.CustomEvent('phoenix:advertising:adhesionWon'));
                    removeFixedBottomAd(fixedBottomAd.slotId);
                } else {
                    // Allow the fixed bottom ad to render and refresh, etc.,
                    // when the adhesion ad response is from backfill or is unfilled.
                    ad = fixedBottomAd;
                }
                deferredZones = undefined;
            } else if (ad === adhesionAd || ad === fixedBottomAd) {
                // we're not ready to pick between adhesion/fixed-bottom yet
                return;
            }
        }
        return handleSlotResult(ad);
    }

    function handleSlotResult(ad) {
        debugLog('begin handleSlotResult for ad', ad);
        const adElement = ad.element;
        let filled = ad.filled;
        const sizeCode = ad.sizeCode;
        const sizeDims = ad.sizeDims;
        /** @type {HTMLElement | null} */
        const slotElement = adElement.parentElement;
        /** @type {HTMLElement | null} */
        const containerElement = slotElement.parentElement;
        const balloonHeader = containerElement?.closest('phoenix-balloon-header');

        // We are manually stopping the ad from showing up
        // on the page
        if (!!session.disableAdRendering) {
            adElement.style.display = 'none';
            return;
        }

        // For exit_interstitial zone, GTM injects the ad at the root of the `html` element,
        // so we don't need to add or remove any class names from the ad container or any of its parents
        if (slotElement.tagName.toUpperCase() === 'HTML') {
            notifySlotResult(ad);
            return;
        }

        slotElement.classList.remove('is-placeholder');

        if (!filled) {
            for (const el of [containerElement, slotElement, balloonHeader]) {
                if (el) {
                    el.classList.add(IS_EMPTY, NOT_FILLED);
                    el.classList.remove(IS_LOADED);
                }
            }
            notifySlotResult(ad);
            return;
        }

        for (const el of [containerElement, slotElement, balloonHeader]) {
            if (el) {
                el.classList.add(IS_LOADED);
                el.classList.remove(IS_EMPTY);
            }
        }

        adElement.setAttribute('data-ad-size', sizeCode);

        const classRulesJson = containerElement.getAttribute('data-class-rules');
        if (classRulesJson) {
            const classRules = JSON.parse(classRulesJson);
            for (let i = 0; i < classRules.length; i += 1) {
                const rule = classRules[i];
                if (
                    (rule.sizes && rule.sizes.indexOf(sizeCode) !== -1) ||
                    (rule.sizes && rule.sizes.indexOf(sizeDims) !== -1) ||
                    (rule.dimensions && rule.dimensions.indexOf(sizeDims) !== -1)
                ) {
                    for (let j = 0; j < rule.classes.length; j += 1) {
                        containerElement.classList.add(rule.classes[j]);
                    }
                } else {
                    // Since the same container can be reused for multiple ad requests we need to make sure
                    // that rules that no longer apply are removed
                    for (let j = 0; j < rule.classes.length; j += 1) {
                        if (containerElement.classList.contains(rule.classes[j]) !== -1) {
                            containerElement.classList.remove(rule.classes[j]);
                        }
                    }
                }
            }
        }

        notifySlotResult(ad);
    }

    function notifySlotResult(ad) {
        // Notify anyone listening for a result for this slot.
        // We'll make a fresh promise for every result even if nobody is
        // already listening, since this avoids a race condition if an
        // ad is loaded before its listener is registered.
        if (ad.slotId) {
            makeSlotPromise(ad.slotId);
            if (pendingSlotDefers[ad.slotId]) {
                // might be already rejected by navigation
                pendingSlotDefers[ad.slotId].resolve(ad);
            }
        }
    }

    function makeSlotPromise(id) {
        debugLog('begin makeSlotPromise for id', id);
        if (!slotPromises[id]) {
            slotPromises[id] = new Promise(function (resolve, reject) {
                pendingSlotDefers[id] = {
                    resolve: resolve,
                    reject: reject,
                };
            }).then(
                // Clean up our stored defer so we won't try to reject it
                // again when the page is "closed".
                function (result) {
                    debugLog('got result for makeSlotPromise', { id, result });
                    delete pendingSlotDefers[id];
                    return result;
                },
                function (err) {
                    debugLog('got error for makeSlotPromise', { id, err });
                    delete pendingSlotDefers[id];
                    throw err;
                }
            );
        }
        return slotPromises[id];
    }
}

module.exports = Session;
